import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from '@nobia/zeus-components/lib/i18n/useTranslation'

export const STAFF_FILTER_ALL = 'all'
export const STAFF_FILTER_MY = 'my'

export const getDefaultStaffFilterKey = (ownStaffId, { includeAllOption = true } = {}) => {
  if (ownStaffId) return STAFF_FILTER_MY
  if (includeAllOption) return STAFF_FILTER_ALL
  return null
}

/**
 * Maps dropdown selection to MultipleCustomersMessages filter.staffId.
 */
export const resolveEffectiveStaffId = (selectedKey, ownStaffId) => {
  if (selectedKey == null) {
    return ''
  }
  if (selectedKey === STAFF_FILTER_ALL) {
    return ''
  }
  if (selectedKey === STAFF_FILTER_MY) {
    return ownStaffId ? String(ownStaffId) : ''
  }
  if (selectedKey !== '') {
    return String(selectedKey)
  }
  return ownStaffId ? String(ownStaffId) : ''
}

export const shouldShowActingBanner = (effectiveStaffId, ownStaffId) => {
  const effective = effectiveStaffId != null ? String(effectiveStaffId) : ''
  const own = ownStaffId != null ? String(ownStaffId) : ''
  return effective !== '' && effective !== own
}

const formatStaffName = (staff) => {
  if (!staff) return ''
  return `${staff.firstName || ''} ${staff.lastName || ''}`.trim()
}

/** When "My" is available, exclude the logged-in designer from per-staff options. */
export const filterStaffMembersExcludingOwn = (staffMembers, ownStaffId) => {
  const own =
    ownStaffId != null && ownStaffId !== '' ? String(ownStaffId) : ''
  if (!own) {
    return staffMembers
  }
  return staffMembers.filter((staff) => String(staff.id) !== own)
}

/**
 * Inbox staff filter for Messages page: All / My / per-designer selection.
 */
export const useMessagesInboxStaffFilter = ({
  storeId,
  ownStaffId,
  staffMembers = [],
  staffListLoading = false,
  includeAllOption = true,
  includeMyOption = true,
  urlSelectedKey = null,
}) => {
  const { t } = useTranslation('pages.customers.messages-page')
  const { t: tFilter } = useTranslation('pages.customers.filter')

  const ownStaffIdStr = ownStaffId != null && ownStaffId !== '' ? String(ownStaffId) : ''

  const normalizedStoreId =
    storeId != null && storeId !== '' ? String(storeId) : ''

  const [selectedKey, setSelectedKey] = useState(null)
  const prevStoreIdRef = useRef(null)

  const isStaffFilterReady = !staffListLoading && selectedKey != null

  useEffect(() => {
    if (staffListLoading) {
      return
    }

    const storeChanged =
      prevStoreIdRef.current != null &&
      prevStoreIdRef.current !== normalizedStoreId
    prevStoreIdRef.current = normalizedStoreId

    if (urlSelectedKey != null) {
      setSelectedKey((current) =>
        String(current) === String(urlSelectedKey) ? current : urlSelectedKey
      )
      return
    }

    if (storeChanged) {
      setSelectedKey(
        getDefaultStaffFilterKey(ownStaffIdStr, { includeAllOption })
      )
      return
    }

    setSelectedKey((current) =>
      current == null
        ? getDefaultStaffFilterKey(ownStaffIdStr, { includeAllOption })
        : current
    )
  }, [
    normalizedStoreId,
    staffListLoading,
    ownStaffIdStr,
    includeAllOption,
    urlSelectedKey,
  ])

  const effectiveStaffId = useMemo(
    () => resolveEffectiveStaffId(selectedKey, ownStaffIdStr),
    [selectedKey, ownStaffIdStr]
  )

  const staffFilterOptions = useMemo(() => {
    const otherStaffMembers = filterStaffMembersExcludingOwn(
      staffMembers,
      ownStaffIdStr
    )

    const allOption = includeAllOption
      ? [
          {
            value: STAFF_FILTER_ALL,
            label: tFilter('staff.all'),
          },
        ]
      : []

    const myOption = includeMyOption
      ? [
          {
            value: STAFF_FILTER_MY,
            label: t('staff.my'),
            disabled: !ownStaffIdStr,
          },
        ]
      : []

    return [
      ...allOption,
      ...myOption,
      ...otherStaffMembers.map((staff) => ({
        value: String(staff.id),
        label: formatStaffName(staff),
      })),
    ]
  }, [staffMembers, t, tFilter, ownStaffIdStr, includeAllOption, includeMyOption])

  const selectedOptionLabel = useMemo(() => {
    if (selectedKey == null) return ''
    const match = staffFilterOptions.find(
      (opt) => String(opt.value) === String(selectedKey)
    )
    return match?.label ?? ''
  }, [staffFilterOptions, selectedKey])

  const actingAsStaff = useMemo(() => {
    if (!shouldShowActingBanner(effectiveStaffId, ownStaffIdStr)) {
      return null
    }
    return (
      staffMembers.find((s) => String(s.id) === String(effectiveStaffId)) ?? null
    )
  }, [effectiveStaffId, ownStaffIdStr, staffMembers])

  const showActingBanner = shouldShowActingBanner(effectiveStaffId, ownStaffIdStr)
  const actingStaffName = useMemo(() => {
    if (!showActingBanner) return ''
    const fromMember = formatStaffName(actingAsStaff)
    if (fromMember) return fromMember
    const fromOption = staffFilterOptions.find(
      (opt) => String(opt.value) === String(effectiveStaffId)
    )
    return fromOption?.label ?? ''
  }, [
    showActingBanner,
    actingAsStaff,
    staffFilterOptions,
    effectiveStaffId,
  ])

  const onStaffFilterChange = useCallback((key) => {
    setSelectedKey(key)
  }, [])

  return {
    selectedKey,
    effectiveStaffId,
    staffFilterOptions,
    selectedOptionLabel,
    onStaffFilterChange,
    showActingBanner,
    actingStaffName,
    staffFilterLoading: staffListLoading,
    isStaffFilterReady,
  }
}

export default useMessagesInboxStaffFilter
